import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import BusTripApi from "Services/BusTrip";
import BusTripCreateModal from "Pages/Modals/BusTrip/Create";
import BusTripDeleteModal from "Pages/Modals/BusTrip/Delete";

const BusTrip = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [addBusTripModal, setAddBusTripModal] = useState(false);
  const [deleteBusTripModal, setDeleteBusTripModal] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState("");

  const normalizeBusTripsList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.trips)) {
      return { rows: payload.trips || [], total: payload?.total || payload?.trips?.length || 0 };
    }
    if (Array.isArray(payload?.data?.trips)) {
      return { rows: payload.data.trips || [], total: payload?.data?.total || payload?.data?.trips?.length || 0 };
    }
    return { rows: [], total: 0 };
  };

  const getBusTrips = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    BusTripApi.getBusTripsList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeBusTripsList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllBusTrips = useCallback(() => {
    BusTripApi.getBusTripsList({ page: 1, limit: 1000, search: "" }).then(
      (res) => {
        const normalized = normalizeBusTripsList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getBusTrips();
    getAllBusTrips();
  }, [getBusTrips, getAllBusTrips]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    tripId: data?.id || "-",
    routeCode: data?.bus_routes?.route_code || data?.route_id || "-",
    origin: data?.bus_routes?.places_bus_routes_origin_place_idToplaces?.name || "-",
    destination: data?.bus_routes?.places_bus_routes_destination_place_idToplaces?.name || "-",
    departureAt: data?.departure_at ? new Date(data.departure_at).toLocaleString() : "-",
    arrivalAt: data?.arrival_at ? new Date(data.arrival_at).toLocaleString() : "-",
    seats: `${data?.available_seats ?? 0}/${data?.total_seats ?? 0}`,
    basePrice: `${data?.base_price || "0"} ${data?.currency || ""}`.trim(),
    status: data?.status || "-",
    raw: data,
  }));

  const formatted = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Trip ID": r.tripId,
    "Route Code": r.routeCode,
    Origin: r.origin,
    Destination: r.destination,
    Departure: r.departureAt,
    Arrival: r.arrivalAt,
    Seats: r.seats,
    "Base Price": r.basePrice,
    Status: r.status,
  }));

  const bulkFormatted = _.orderBy(bulkrow || [], ["departure_at"], ["asc"]).map((data, index) => ({
    "S.NO": index + 1,
    "Trip ID": data?.id || "-",
    "Route Code": data?.bus_routes?.route_code || data?.route_id || "-",
    Origin: data?.bus_routes?.places_bus_routes_origin_place_idToplaces?.name || "-",
    Destination: data?.bus_routes?.places_bus_routes_destination_place_idToplaces?.name || "-",
    Departure: data?.departure_at ? new Date(data.departure_at).toLocaleString() : "-",
    Arrival: data?.arrival_at ? new Date(data.arrival_at).toLocaleString() : "-",
    Seats: `${data?.available_seats ?? 0}/${data?.total_seats ?? 0}`,
    "Base Price": `${data?.base_price || "0"} ${data?.currency || ""}`.trim(),
    Status: data?.status || "-",
  }));

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleAdd = () => {
    setSelectedTripId("");
    setAddBusTripModal(true);
  };
  const handleEdit = (tripRow) => {
    const tripId = tripRow?.id || tripRow?._id;
    if (!tripId) {
      dispatch(
        showSnackbar({
          message: "Trip ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedTripId(tripId);
    setAddBusTripModal(true);
  };
  const handleDelete = (tripRow) => {
    const tripId = tripRow?.id || tripRow?._id;
    if (!tripId) {
      dispatch(
        showSnackbar({
          message: "Trip ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedTripId(tripId);
    setDeleteBusTripModal(true);
  };
  const handleCloseAdd = (created = false) => {
    setAddBusTripModal(false);
    setSelectedTripId("");
    if (created) {
      getBusTrips();
      getAllBusTrips();
    }
  };
  const handleCloseDelete = (deleted = false) => {
    setDeleteBusTripModal(false);
    setSelectedTripId("");
    if (deleted) {
      getBusTrips();
      getAllBusTrips();
    }
  };

  const handleCopy = () => {
    const header = Object.keys(formatted[0] || {}).join("\t");
    const rows = formatted.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BusTrips");
    XLSX.writeFile(workbook, "BusTripList.xlsx");
  };
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "BusTripList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.autoTable({
      head: [["S.NO", "Trip ID", "Route Code", "Origin", "Destination", "Departure", "Arrival", "Seats", "Base Price", "Status"]],
      body: formatted.map((r) => Object.values(r)),
      styles: { fontSize: 8 },
    });
    doc.save("BusTripList.pdf");
  };
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All BusTripList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeStyle = (value) => {
    const key = String(value || "").toLowerCase();
    const map = {
      approved: { bg: "#14b8a6", fg: "#ffffff" },
      active: { bg: "#10b981", fg: "#ffffff" },
      published: { bg: "#10b981", fg: "#ffffff" },
      confirmed: { bg: "#10b981", fg: "#ffffff" },
      completed: { bg: "#0ea5e9", fg: "#ffffff" },
      onboarding: { bg: "#ef4444", fg: "#ffffff" },
      boarding: { bg: "#ef4444", fg: "#ffffff" },
      cancelled: { bg: "#ef4444", fg: "#ffffff" },
      rejected: { bg: "#ef4444", fg: "#ffffff" },
      pending: { bg: "#f59e0b", fg: "#111827" },
      draft: { bg: "#6b7280", fg: "#ffffff" },
      full: { bg: "#7c3aed", fg: "#ffffff" },
      departed: { bg: "#2563eb", fg: "#ffffff" },
      arrived: { bg: "#14b8a6", fg: "#ffffff" },
      inactive: { bg: "#9ca3af", fg: "#111827" },
    };
    return map[key] || { bg: "#64748b", fg: "#ffffff" };
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    { field: "routeCode", headerName: "Route Code", minWidth: 130, sortable: true },
    { field: "origin", headerName: "Origin", minWidth: 180, sortable: true },
    { field: "destination", headerName: "Destination", minWidth: 180, sortable: true },
    { field: "departureAt", headerName: "Departure", minWidth: 170, sortable: true },
    { field: "arrivalAt", headerName: "Arrival", minWidth: 170, sortable: true },
    { field: "seats", headerName: "Seats", minWidth: 100, sortable: false },
    { field: "basePrice", headerName: "Base Price", minWidth: 120, sortable: false },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      sortable: true,
      renderCell: (params) => {
        const tone = getStatusBadgeStyle(params?.value);
        return <span style={{ background: tone.bg, color: tone.fg, borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", lineHeight: "16px" }}>{String(params?.value || "-")}</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button variant="contained" color="primary" size="small" style={{ minWidth: 36, padding: "4px 8px" }} onClick={() => handleEdit(params?.row?.raw)}>
            <Edit fontSize="small" />
          </Button>
          <Button variant="contained" color="error" size="small" style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }} onClick={() => handleDelete(params?.row?.raw)}>
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={6} md={6} className={classes.addBtn}>
        <Button variant="contained" onClick={handleAdd}>Add Bus Trip</Button>
      </Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => {
              const p = parseInt(newPage, 10) + 1;
              if (p >= 1) setPage(p);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => setSearchText(model?.quickFilterValues?.join(" ") || "")}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>
      <BusTripCreateModal openDialog={addBusTripModal} handleClose={handleCloseAdd} tripId={selectedTripId} />
      <BusTripDeleteModal openDialog={deleteBusTripModal} handleClose={handleCloseDelete} tripId={selectedTripId} />
    </Grid>
  );
};

export default BusTrip;
